import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "ps_access_token";
const REFRESH_KEY = "ps_refresh_token";
const LAST_ACTIVITY_KEY = "ps_last_activity";

// Renovación silenciosa: cada vez que el usuario está activo, se renueva el
// access token. Si no usa la app por 30 días → caduca el refresh y vuelve a login.
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;       // intentar refresh cada 5 min
const IDLE_THRESHOLD_MS = 30 * 60 * 1000;        // considerar "activo" si hay actividad en últimos 30 min

function _pingActivity() {
  try { localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())); } catch (_) { /* */ }
}

function _isRecentlyActive() {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!raw) return true; // primera carga, asumir activo
    const last = parseInt(raw, 10);
    if (!Number.isFinite(last)) return true;
    return Date.now() - last < IDLE_THRESHOLD_MS;
  } catch { return true; }
}

export function setAccessToken(token) {
  if (token) {
    try { localStorage.setItem(TOKEN_KEY, token); } catch (_) { /* */ }
    _pingActivity();
  } else {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      localStorage.removeItem(LAST_ACTIVITY_KEY);
    } catch (_) { /* */ }
  }
}

export function setRefreshToken(token) {
  try {
    if (token) localStorage.setItem(REFRESH_KEY, token);
    else localStorage.removeItem(REFRESH_KEY);
  } catch (_) { /* */ }
}

export function getAccessToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

export function getRefreshToken() {
  try { return localStorage.getItem(REFRESH_KEY); } catch { return null; }
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Inject Bearer token from localStorage as fallback when cookies are blocked
// (e.g. cross-origin SameSite=Lax cookies stripped between erp.pharma-stern.com
// and laughing-aryabhata-4.emergent.host). Backend accepts both cookie + header.
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ===== Silent refresh: llamada compartida y reintentos en 401 =====
let _refreshing = null;

/**
 * Renueva el access token.
 * Devuelve "ok" | "invalid" (refresh token rechazado) | "unavailable"
 * (servidor caído / 502-503-504 / sin red). ITER207: un 503 transitorio NO debe
 * cerrar la sesión del usuario.
 */
async function _doRefresh() {
  if (_refreshing) return _refreshing;
  _refreshing = (async () => {
    try {
      const headers = {};
      const rt = getRefreshToken();
      if (rt) headers["X-Refresh-Token"] = rt;
      const resp = await axios.post(`${API}/auth/refresh`, {}, {
        withCredentials: true,
        headers,
      });
      const data = resp.data || {};
      if (data.access_token) setAccessToken(data.access_token);
      if (data.refresh_token) setRefreshToken(data.refresh_token);
      return "ok";
    } catch (err) {
      const st = err?.response?.status;
      if (st === 401 || st === 403) return "invalid";
      return "unavailable";
    } finally {
      _refreshing = null;
    }
  })();
  return _refreshing;
}

// Reintentar UNA vez tras 401 silencioso
api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config || {};
    const url = original.url || "";
    const status = error?.response?.status;
    // Evitar bucle infinito en endpoints de auth
    const isAuthEndpoint = url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/logout");
    if (status === 401 && !original._retried && !isAuthEndpoint) {
      original._retried = true;
      const res = await _doRefresh();
      if (res === "ok") {
        const newToken = getAccessToken();
        if (newToken) original.headers["Authorization"] = `Bearer ${newToken}`;
        return api.request(original);
      }
      // Solo cerrar sesión si el refresh token fue RECHAZADO. Si el servidor
      // no está disponible (503/502/504/sin red) conservamos los tokens.
      if (res === "invalid") setAccessToken(null);
    }
    return Promise.reject(error);
  }
);

// Refresh proactivo: cada 5 min si el usuario sigue activo
if (typeof window !== "undefined") {
  _pingActivity();
  ["mousemove", "keydown", "click", "touchstart", "scroll"].forEach((ev) =>
    window.addEventListener(ev, _pingActivity, { passive: true })
  );
  setInterval(() => {
    if (getAccessToken() && _isRecentlyActive()) {
      _doRefresh();
    }
  }, REFRESH_INTERVAL_MS);
  // Al regresar tras background prolongado, intentar refresh inmediato
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && getAccessToken() && _isRecentlyActive()) {
      _doRefresh();
    }
  });
}

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Error inesperado";
  if (typeof detail === "string") return detail;
  const formatItem = (e) => {
    if (!e) return "";
    const msg = (e && typeof e.msg === "string") ? e.msg : JSON.stringify(e);
    // Pydantic errors include loc: ["body","lineas",0,"producto_nombre"]
    if (Array.isArray(e.loc) && e.loc.length > 1) {
      const path = e.loc.slice(1).join(".");
      return `${path}: ${msg}`;
    }
    return msg;
  };
  if (Array.isArray(detail))
    return detail.map(formatItem).filter(Boolean).join(" · ");
  if (detail && typeof detail.msg === "string") return formatItem(detail);
  return String(detail);
}


/**
 * Descarga/abre un archivo (PDF, XML, etc) desde la API autenticada y lo abre
 * en una nueva pestaña como blob URL. Esto evita el bug "No autenticado"
 * cuando `window.open(absoluteUrl)` no envía el Authorization Bearer.
 *
 * @param {string} path - ruta relativa a /api (ej: "/invoicing/facturas/123/ride")
 * @param {object} opts - { filename, mime, downloadOnly }
 * @returns {Promise<void>}
 */
export async function openApiFile(path, opts = {}) {
  const { filename = "documento", mime = "application/pdf", downloadOnly = false } = opts;
  try {
    const { data } = await api.get(path, { responseType: "blob" });
    const blob = new Blob([data], { type: mime });
    const url = URL.createObjectURL(blob);
    if (downloadOnly) {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
    } else {
      const win = window.open(url, "_blank");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
      }
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    // El cuerpo de error viene como Blob (responseType=blob).
    // Convertirlo a texto para que formatApiError pueda procesarlo.
    if (err?.response?.data instanceof Blob) {
      try {
        const txt = await err.response.data.text();
        try {
          err.response.data = JSON.parse(txt);
        } catch {
          err.response.data = { detail: txt || `HTTP ${err.response.status}` };
        }
      } catch {
        err.response.data = { detail: `HTTP ${err.response.status || "error"}` };
      }
    }
    throw err;
  }
}

/**
 * Comparte un PDF descargado desde la API a través del share-sheet nativo del SO
 * (Web Share API Level 2). En móviles modernos se abre el menú nativo donde el
 * usuario puede elegir WhatsApp, Mail, Drive, etc. En desktop o cuando el share
 * con archivos no está soportado, hace fallback a descarga directa.
 *
 * @param {string} path     ruta relativa a /api (ej: "/sales/pedidos/123/pdf")
 * @param {object} opts     { filename, title, text }
 * @returns {Promise<{shared: boolean, fallback: string|null}>}
 */
export async function shareApiPdf(path, opts = {}) {
  const { filename = "documento.pdf", title = "Documento", text = "" } = opts;
  const { data } = await api.get(path, { responseType: "blob" });
  const blob = new Blob([data], { type: "application/pdf" });
  const file = new File([blob], filename, { type: "application/pdf" });

  // Web Share API Level 2 (móvil moderno)
  if (typeof navigator !== "undefined" && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title, text });
      return { shared: true, fallback: null };
    } catch (err) {
      // AbortError: el usuario canceló el sheet. No es un error real.
      if (err && err.name === "AbortError") return { shared: false, fallback: "cancelled" };
      // Cualquier otro error: caer al fallback de descarga
    }
  }

  // Fallback: descargar el PDF para que el usuario lo adjunte manualmente
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return { shared: false, fallback: "downloaded" };
}
